import React, { useState, useEffect } from 'react'
import { ModalBase } from './ModalBase'

interface BookingModalProps {
    isOpen: boolean
    onClose: () => void
}

export const BookingModal: React.FC<BookingModalProps> = ({ isOpen, onClose }) => {
    const [step, setStep] = useState<'date' | 'success' | 'waitlist'>('date')
    const [selectedDate, setSelectedDate] = useState<number | null>(null)

    useEffect(() => {
        if (isOpen) {
            setStep('date')
            setSelectedDate(null)
        }
    }, [isOpen])

    // Simulate availability: slot 2 is Full
    const dates = Array.from({ length: 6 }, (_, i) => ({
        day: i + 1,
        isFull: i === 1
    }))

    const handleBooking = () => {
        const dateObj = dates.find(d => d.day === selectedDate)
        if (dateObj?.isFull) {
            setStep('waitlist')
        } else {
            setStep('success')
        }
    }

    if (step === 'success') {
        return (
            <ModalBase isOpen={isOpen} onClose={onClose}>
                <div className="flex flex-col items-center text-center pt-8 pb-4">
                    <div className="size-24 bg-primary/20 rounded-full flex items-center justify-center mb-6 text-primary">
                        <span className="material-symbols-outlined text-[48px] filled">event_available</span>
                    </div>
                    <h3 className="text-2xl font-bold text-text-main dark:text-white mb-2">Booking Confirmed!</h3>
                    <p className="text-gray-500 dark:text-text-muted dark:text-gray-400 mb-6">
                        Your field practice session has been scheduled.
                    </p>

                    <div className="bg-white dark:bg-white/5 border border-dashed border-gray-300 dark:border-white/20 p-4 rounded-lg w-full mb-6 relative overflow-hidden">
                        <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-background-light dark:bg-surface-card rounded-full" />
                        <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-background-light dark:bg-surface-card rounded-full" />
                        <div className="text-center">
                            <p className="text-xs uppercase tracking-widest text-gray-500 dark:text-text-muted">Ticket Code</p>
                            <p className="text-2xl font-mono font-bold tracking-widest mt-1">XP-{Math.floor(Math.random() * 9999)}</p>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="w-full py-4 bg-primary text-black font-bold rounded-full mb-3 shadow-lg active:scale-[0.98] transition-transform"
                    >
                        Close
                    </button>
                </div>
            </ModalBase>
        )
    }

    if (step === 'waitlist') {
        return (
            <ModalBase isOpen={isOpen} onClose={onClose}>
                <div className="flex flex-col items-center text-center pt-8 pb-4">
                    <div className="size-24 bg-orange-500/20 rounded-full flex items-center justify-center mb-6 text-orange-500">
                        <span className="material-symbols-outlined text-[48px] filled">hourglass_top</span>
                    </div>
                    <h3 className="text-2xl font-bold text-text-main dark:text-white mb-2">Slot Full</h3>
                    <p className="text-gray-500 dark:text-text-muted dark:text-gray-400 mb-6">
                        The selected slot is currently fully booked. Would you like to join the waitlist?
                    </p>
                    <button
                        onClick={() => setStep('success')}
                        className="w-full py-4 bg-white dark:bg-surface-card border-2 border-orange-500 text-orange-500 font-bold rounded-full mb-3 shadow-sm active:scale-[0.98] transition-transform"
                    >
                        Join Waitlist
                    </button>
                    <button
                        onClick={() => setStep('date')}
                        className="w-full py-4 text-gray-500 dark:text-text-muted font-bold rounded-full transition-transform"
                    >
                        Choose Another Date
                    </button>
                </div>
            </ModalBase>
        )
    }

    return (
        <ModalBase isOpen={isOpen} onClose={onClose}>
            <h3 className="text-xl font-bold tracking-tight text-text-main dark:text-white mb-2">
                Book Field Session
            </h3>
            <p className="text-sm text-gray-500 dark:text-text-muted mb-6">Select a date for your practical exam.</p>

            <div className="grid grid-cols-3 gap-3 mb-8">
                {dates.map((d) => (
                    <button
                        key={d.day}
                        onClick={() => setSelectedDate(d.day)}
                        className={`relative flex flex-col items-center justify-center p-4 rounded-lg border transition-all 
              ${selectedDate === d.day
                                ? (d.isFull ? 'bg-orange-500 border-orange-500 text-white shadow-md' : 'bg-primary border-primary text-black shadow-md')
                                : 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 hover:border-primary/50'}`}
                    >
                        {d.isFull && (
                            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                                FULL
                            </span>
                        )}
                        <span className="text-xs font-bold uppercase opacity-60">JAN</span>
                        <span className="text-2xl font-bold">{10 + d.day}</span>
                        <span className="text-xs mt-1">09:00 AM</span>
                    </button>
                ))}
            </div>

            <button
                disabled={!selectedDate}
                onClick={handleBooking}
                className="w-full py-4 bg-primary text-black font-bold rounded-full shadow-lg hover:shadow-primary/30 hover:scale-[1.01] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {dates.find(d => d.day === selectedDate)?.isFull ? 'Join Waitlist' : 'Confirm Booking'}
            </button>
        </ModalBase>
    )
}

export default BookingModal
